import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"

import { DashboardLayout } from "../../layouts/DashboardLayout"
import {
  getAdminLessonApi,
  getLessonApi,
  updateLessonApi,
  updateMentorLessonApi,
  AdminLessonDetail,
  LessonType,
} from "../../features/lessons/api/lesson.api"
import { VideoUploadField } from "../../features/lessons/ui/VideoUploadField"
import { LessonVideoPlayer } from "../../features/lessons/ui/LessonVideoPlayer"
import {
  ChevronLeftIcon,
  ClockIcon,
  LockClosedIcon,
  GiftIcon,
  FilmIcon,
  DocumentTextIcon,
  PaperClipIcon,
  ArrowTopRightOnSquareIcon,
  PencilSquareIcon,
  CheckCircleIcon,
} from "@heroicons/react/16/solid"

/* ── helpers ─────────────────────────────────────────── */

const TypeConfig: Record<string, { label: string; icon: React.ReactNode; className: string }> = {
  video: {
    label: "Видео",
    icon: <FilmIcon className="w-3.5 h-3.5" />,
    className: "bg-blue-50 text-blue-700 ring-1 ring-blue-200",
  },
  text: {
    label: "Текст",
    icon: <DocumentTextIcon className="w-3.5 h-3.5" />,
    className: "bg-gray-100 text-gray-600 ring-1 ring-gray-200",
  },
}

/* ── page ─────────────────────────────────────────────── */

export const LessonDetailPage = () => {
  const { moduleId, lessonId } = useParams<{ moduleId: string; lessonId: string }>()
  const navigate = useNavigate()
  const isAdmin = localStorage.getItem("role") === "admin"

  const [lesson, setLesson] = useState<AdminLessonDetail | null>(null)
  const [loading, setLoading] = useState(true)

  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState("")
  const [form, setForm] = useState({
    title: "",
    type: "text" as LessonType,
    content: "",
    videoId: "",
    duration: "",
    orderNumber: 0,
    accessType: "paid" as "free" | "paid",
    isActive: true,
  })

  useEffect(() => {
    if (!lessonId) return
    setLoading(true)

    const request = isAdmin
      ? getAdminLessonApi(lessonId).then((res) => ({ ...res.data, files: [] }))
      : moduleId
        ? getLessonApi(moduleId, lessonId).then((res) => ({
            id: res.data.id,
            module_id: res.data.module_id,
            title: res.data.title,
            type: res.data.type,
            access_type: res.data.access_type,
            content: res.data.content,
            video_id: null as string | null,
            duration: res.data.duration,
            order_number: res.data.order_number,
            is_active: res.data.is_active ?? true,
            files: res.data.files ?? [],
          }))
        : Promise.reject(new Error("missing moduleId"))

    request
      .then((data) => setLesson(data))
      .catch(() => setLesson(null))
      .finally(() => setLoading(false))
  }, [lessonId, moduleId, isAdmin])

  const startEdit = () => {
    if (!lesson) return
    setForm({
      title: lesson.title,
      type: (lesson.type as LessonType) ?? "text",
      content: lesson.content ?? "",
      videoId: lesson.video_id ?? "",
      duration: lesson.duration ?? "",
      orderNumber: lesson.order_number,
      accessType: (lesson.access_type as "free" | "paid") ?? "paid",
      isActive: lesson.is_active ?? true,
    })
    setSaveError("")
    setEditing(true)
  }

  const handleVideoReady = async (videoId: string) => {
    setForm((f) => ({ ...f, videoId }))
    if (!lessonId) return
    try {
      if (isAdmin) {
        await updateLessonApi(lessonId, { video_id: videoId })
      } else {
        await updateMentorLessonApi(lessonId, { video_id: videoId })
      }
      setLesson((prev) => (prev ? { ...prev, video_id: videoId } : prev))
    } catch {
      // attaching failed silently — the regular "Сохранить" will retry it
    }
  }

  const handleSave = async () => {
    if (!lesson || !lessonId) return
    if (!form.title.trim()) return
    setSaving(true)
    setSaveError("")
    try {
      if (isAdmin) {
        await updateLessonApi(lessonId, {
          title: form.title,
          type: form.type,
          content: form.content,
          ...(form.videoId.trim() ? { video_id: form.videoId.trim() } : {}),
          duration: form.duration,
          order_number: form.orderNumber,
          access_type: form.accessType,
          is_active: form.isActive,
        })
      } else {
        await updateMentorLessonApi(lessonId, {
          title: form.title,
          type: form.type,
          content: form.content,
          ...(form.videoId.trim() ? { video_id: form.videoId.trim() } : {}),
          duration: form.duration,
          order_number: form.orderNumber,
        })
      }
      setLesson({
        ...lesson,
        title: form.title,
        type: form.type,
        content: form.content,
        ...(form.videoId.trim() ? { video_id: form.videoId.trim() } : {}),
        duration: form.duration,
        order_number: form.orderNumber,
        ...(isAdmin ? { access_type: form.accessType, is_active: form.isActive } : {}),
      })
      setEditing(false)
    } catch {
      setSaveError("Не удалось сохранить изменения")
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <DashboardLayout title="Урок">
        <div className="max-w-4xl mx-auto space-y-4 animate-pulse">
          <div className="h-32 rounded-xl bg-gray-100" />
          <div className="h-64 rounded-xl bg-gray-100" />
        </div>
      </DashboardLayout>
    )
  }

  if (!lesson) {
    return (
      <DashboardLayout title="Урок">
        <div className="py-20 text-center text-gray-400">Урок не найден</div>
      </DashboardLayout>
    )
  }

  const typeCfg = TypeConfig[lesson.type] ?? TypeConfig.text

  return (
    <DashboardLayout title={lesson.title}>
      <div className="max-w-4xl mx-auto space-y-4">

        {/* ── HEADER ── */}
        <div className="bg-white rounded-xl border p-6">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-blue-500 transition-colors mb-4"
            >
              <ChevronLeftIcon className="w-3.5 h-3.5" />
              Назад
            </button>

            {!editing && (
              <button
                onClick={startEdit}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-blue-600 px-2.5 py-1.5 rounded-lg hover:bg-blue-50 transition-colors mb-4"
              >
                <PencilSquareIcon className="w-3.5 h-3.5" />
                Редактировать
              </button>
            )}
          </div>

          <h1 className="text-xl font-bold text-gray-900">{lesson.title}</h1>

          <div className="flex flex-wrap gap-2 mt-3">
            {/* Type */}
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${typeCfg.className}`}>
              {typeCfg.icon}
              {typeCfg.label}
            </span>

            {/* Duration */}
            {lesson.duration && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                <ClockIcon className="w-3.5 h-3.5" />
                {lesson.duration}
              </span>
            )}

            {/* Access */}
            {lesson.access_type === "free" ? (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200">
                <GiftIcon className="w-3.5 h-3.5" />
                Бесплатно
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                <LockClosedIcon className="w-3.5 h-3.5" />
                Платно
              </span>
            )}
          </div>
        </div>

        {/* ── VIDEO ── */}
        {!editing && lesson.type === "video" && lessonId && (
          <div className="bg-white rounded-xl border p-6">
            <h2 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <FilmIcon className="w-4 h-4 text-gray-400" />
              Видео
            </h2>
            <LessonVideoPlayer lessonId={lessonId} />
          </div>
        )}

        {/* ── CONTENT / EDIT ── */}
        {!editing ? (
          <div className="bg-white rounded-xl border p-6">
            <h2 className="text-sm font-semibold text-gray-700 mb-4">Содержимое</h2>
            {lesson.content ? (
              <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                {lesson.content}
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic">Нет содержимого</p>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-xl border p-6 space-y-4">
            <h2 className="text-sm font-semibold text-gray-700">Редактировать урок</h2>

            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Название</label>
              <input
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Тип</label>
                <select
                  value={form.type}
                  onChange={(e) => setForm((f) => ({ ...f, type: e.target.value as LessonType }))}
                  className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="text">Текст</option>
                  <option value="video">Видео</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Длительность</label>
                <input
                  value={form.duration}
                  onChange={(e) => setForm((f) => ({ ...f, duration: e.target.value }))}
                  placeholder="например, 12:30"
                  className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {form.type === "video" && lessonId && (
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Видео</label>
                <VideoUploadField
                  lessonId={lessonId}
                  videoId={form.videoId.trim() || null}
                  lessonTitle={form.title}
                  onVideoReady={handleVideoReady}
                />
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Содержимое</label>
              <textarea
                value={form.content}
                onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
                rows={6}
                className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Порядок</label>
              <input
                type="number"
                value={form.orderNumber}
                onChange={(e) => setForm((f) => ({ ...f, orderNumber: Number(e.target.value) }))}
                className="w-32 border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {isAdmin && (
              <div className="grid grid-cols-2 gap-3 pt-1 border-t">
                <div className="pt-3">
                  <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Доступ</label>
                  <select
                    value={form.accessType}
                    onChange={(e) => setForm((f) => ({ ...f, accessType: e.target.value as "free" | "paid" }))}
                    className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="paid">Платно</option>
                    <option value="free">Бесплатно</option>
                  </select>
                </div>
                <div className="pt-3 flex items-end">
                  <label className="flex items-center gap-2 text-sm text-gray-600">
                    <input
                      type="checkbox"
                      checked={form.isActive}
                      onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                    />
                    Активен
                  </label>
                </div>
              </div>
            )}

            {saveError && (
              <div className="px-4 py-3 rounded-xl text-sm font-medium bg-red-50 text-red-700 border border-red-200">
                {saveError}
              </div>
            )}

            <div className="flex gap-2 pt-1">
              <button
                onClick={() => setEditing(false)}
                className="flex-1 py-2.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
              >
                Отмена
              </button>
              <button
                disabled={saving || !form.title.trim()}
                onClick={handleSave}
                className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white py-2.5 rounded-xl text-sm font-semibold transition-colors shadow-sm shadow-blue-200"
              >
                {saving
                  ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  : <CheckCircleIcon className="w-4 h-4" />
                }
                Сохранить
              </button>
            </div>
          </div>
        )}

        {/* ── FILES ── */}
        {lesson.files && lesson.files.length > 0 && (
          <div className="bg-white rounded-xl border p-6">
            <h2 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <PaperClipIcon className="w-4 h-4 text-gray-400" />
              Вложения
              <span className="bg-gray-100 text-gray-500 text-xs px-2 py-0.5 rounded-full font-medium">
                {lesson.files.length}
              </span>
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {lesson.files.map((f) => (
                <a
                  key={f.id}
                  href={f.url}
                  target="_blank"
                  rel="noreferrer"
                  className="group flex items-center justify-between rounded-xl border border-gray-100 px-4 py-3 hover:border-blue-200 hover:bg-blue-50/30 transition-all"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                      <PaperClipIcon className="w-4 h-4 text-gray-400" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{f.filename}</p>
                      <p className="text-xs text-gray-400">{(f.size / 1024).toFixed(1)} КБ</p>
                    </div>
                  </div>
                  <ArrowTopRightOnSquareIcon className="w-4 h-4 text-gray-300 group-hover:text-blue-500 transition-colors flex-shrink-0 ml-2" />
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  )
}
